import { useEffect, useState, useMemo } from "react";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  ArrowRight, 
  User, 
  Calendar,
  Eye,
  Plus
} from "lucide-react";
import { opportunityAPI, Opportunity, QuoteData } from "@/services/opportunityService";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { OpportunityDetail } from "@/components/opportunities/OpportunityDetail";
import { QuoteViewer } from "@/components/opportunities/QuoteViewer";
import { TablePageSkeleton } from "@/components/shared/TablePageSkeleton";

export default function ApprovalsPage() {
  const { company, isLoading: isAuthLoading } = useAuth();
  const slug = company?.slug;
  const { toast } = useToast();
  const [pendingApprovals, setPendingApprovals] = useState<Opportunity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOppId, setSelectedOppId] = useState<string | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // Misma lógica que OpportunityDetail para el símbolo de moneda
  const currencySymbol = useMemo(() => {
    if ((company as any)?.currency) return (company as any).currency;
    try {
      const storedCompany = localStorage.getItem("company");
      if (storedCompany) {
        const parsed = JSON.parse(storedCompany);
        if (parsed.currency) return parsed.currency;
      }
    } catch (e) {}
    return "$";
  }, [company]);

  const formatCurrency = (value: number | string | undefined | null) => {
    if (value === undefined || value === null) return "0,00";
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return "0,00";
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  useEffect(() => {
    if (slug) {
      fetchPendingApprovals();
    } else if (!isAuthLoading && !slug) {
      setIsLoading(false);
    }
  }, [slug, isAuthLoading]);

  const fetchPendingApprovals = async () => {
    if (!slug) return;
    try {
      setIsLoading(true);
      const res = await opportunityAPI.getPendingApprovals(slug);
      if (res.success) {
        setPendingApprovals(res.data);
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };


  const getOpportunityTotals = (opportunity: Opportunity) => {
    if (!opportunity.items || opportunity.items.length === 0) {
      return { baseOriginal: Number(opportunity.total), totalIncrease: 0, totalDiscount: 0, requested: Number(opportunity.total) };
    }
    
    let baseOriginal = 0;
    let totalIncrease = 0;
    let totalDiscount = 0;
    
    opportunity.items.forEach(item => {
      const originalPrice = item.original_price ? Number(item.original_price) : Number(item.base_price);
      const requestedPrice = Number(item.base_price);
      const qty = Number(item.quantity);
      
      // Diferencia en precio base
      const priceDiff = (requestedPrice - originalPrice) * qty;
      if (priceDiff > 0) totalIncrease += priceDiff;
      else if (priceDiff < 0) totalDiscount += Math.abs(priceDiff);
      
      baseOriginal += (originalPrice * qty);
      
      // Extras/Reglas
      const lineTotal = Number(item.line_total) || (requestedPrice * qty);
      const extrasDiff = (lineTotal - (requestedPrice * qty));
      if (extrasDiff > 0) totalIncrease += extrasDiff;
      else if (extrasDiff < 0) totalDiscount += Math.abs(extrasDiff);
    });
    
    return { baseOriginal, totalIncrease, totalDiscount, requested: Number(opportunity.total) };
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <TablePageSkeleton hasMetrics={false} columnCount={9} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Centro de Aprobaciones</h1>
          <p className="text-slate-500 mt-1">Revisa y gestiona las desviaciones de precios solicitadas.</p>
        </div>
        <Badge variant="outline" className="px-3 py-1 bg-amber-50 text-amber-700 border-amber-200">
          {pendingApprovals.length} Pendientes
        </Badge>
      </div>

      <div className="grid gap-6">
        {pendingApprovals.length === 0 ? (
          <Card className="border-dashed border-2 bg-slate-50/50">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <CheckCircle className="h-12 w-12 text-slate-300 mb-4" />
              <p className="text-slate-600 font-medium text-lg">No hay aprobaciones pendientes</p>
              <p className="text-slate-400 text-sm">¡Buen trabajo! Todo está al día.</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="shadow-sm border-slate-200">
            <CardHeader className="bg-slate-50/50 border-b">
              <CardTitle className="text-lg">Solicitudes de Revisión</CardTitle>
              <CardDescription>Cotizaciones con precios modificados que requieren autorización.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader className="bg-slate-50/50">
                  <TableRow>
                    <TableHead className="w-[250px]">Oportunidad / Cliente</TableHead>
                    <TableHead>Solicitado por</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Desviaciones en Ítems</TableHead>
                    <TableHead className="text-right">Total con Precios Base Originales</TableHead>
                    <TableHead className="text-right text-emerald-600">Incremento (+)</TableHead>
                    <TableHead className="text-right text-amber-600">Descuento (-)</TableHead>
                    <TableHead className="text-right font-bold text-slate-900">Total Cotización</TableHead>
                    <TableHead className="text-right">Acción</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingApprovals.map((opp) => {
                    const { baseOriginal, totalIncrease, totalDiscount, requested } = getOpportunityTotals(opp);

                    return (
                      <TableRow key={opp.id} className="hover:bg-slate-50/80 transition-colors">
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-bold text-slate-900">{opp.name}</span>
                                <Badge variant="outline" className="text-[10px] h-4 px-1 py-0 border-blue-200 text-blue-700 bg-blue-50 ml-2">
                                  V{opp.version_number}.{opp.minor_version || 0}
                                </Badge>
                            <span className="text-xs text-slate-500">{opp.client_company?.name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <User className="h-4 w-4 text-slate-400" />
                            {opp.creator?.first_name} {opp.creator?.last_name}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Calendar className="h-4 w-4 text-slate-400" />
                            {new Date(opp.created_at).toLocaleDateString()}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1.5 min-w-[200px]">
                            {(() => {
                              const deviatedItems = opp.items?.filter(i => {
                                const originalPrice = i.original_price ? Number(i.original_price) : Number(i.base_price);
                                const requestedPrice = Number(i.base_price);
                                const qty = Number(i.quantity);
                                const lineTotal = Number(i.line_total) || (requestedPrice * qty);
                                
                                const hasBaseDeviation = requestedPrice !== originalPrice;
                                const hasExtras = lineTotal !== (requestedPrice * qty);
                                return hasBaseDeviation || hasExtras;
                              }) || [];

                              if (!deviatedItems.length) {
                                return <span className="text-slate-400 text-xs italic">Sin desviaciones ni extras específicos</span>;
                              }

                              return deviatedItems.map(item => {
                                const originalPrice = item.original_price ? Number(item.original_price) : Number(item.base_price);
                                const requestedPrice = Number(item.base_price);
                                const qty = Number(item.quantity);
                                const lineTotal = Number(item.line_total) || (requestedPrice * qty);
                                
                                const hasBaseDeviation = requestedPrice !== originalPrice;
                                const hasExtras = lineTotal !== (requestedPrice * qty);

                                 return (
                                  <div key={item.id} className={`text-xs flex flex-col gap-1.5 ${hasBaseDeviation ? 'bg-amber-50/30 border-amber-100' : 'bg-slate-50/50 border-slate-200'} p-2 rounded-lg border shadow-sm`}>
                                    <div className="flex items-center justify-between gap-1.5">
                                      <div className="flex items-center gap-1.5 overflow-hidden">
                                        {hasBaseDeviation ? <AlertCircle className="h-3 w-3 text-amber-500 flex-shrink-0" /> : <Plus className="h-3 w-3 text-purple-500 flex-shrink-0" />}
                                        <span className="font-bold text-slate-800 truncate" title={item.product?.name || "Producto"}>
                                          {item.product?.name || "Producto"}
                                        </span>
                                      </div>
                                      <span className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-500 font-medium">Cant: {qty}</span>
                                    </div>

                                    <div className="grid grid-cols-3 gap-2 py-1.5 border-y border-slate-100/50 mt-1">
                                      <div className="flex flex-col">
                                        <span className="text-[8px] text-slate-400 font-bold uppercase tracking-wider">Precio Base</span>
                                        <span className="text-slate-600 font-medium">{opp.currency?.symbol}{formatCurrency(originalPrice)}</span>
                                      </div>
                                      <div className="flex flex-col">
                                        <span className="text-[8px] text-slate-400 font-bold uppercase tracking-wider">Variación</span>
                                        <span className={`font-bold ${requestedPrice < originalPrice ? 'text-amber-600' : requestedPrice > originalPrice ? 'text-emerald-600' : 'text-slate-400'}`}>
                                          {requestedPrice < originalPrice ? '-' : requestedPrice > originalPrice ? '+' : ''}{opp.currency?.symbol}{formatCurrency(Math.abs(requestedPrice - originalPrice))}
                                        </span>
                                      </div>
                                      <div className="flex flex-col items-end">
                                        <span className="text-[8px] text-slate-400 font-bold uppercase tracking-wider">Total Ítem</span>
                                        <span className="font-black text-slate-900">{opp.currency?.symbol}{formatCurrency(lineTotal)}</span>
                                      </div>
                                    </div>

                                    {hasExtras && item.applied_rules && item.applied_rules.length > 0 ? (
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {item.applied_rules.map((appliedRule: any, idx: number) => {
                                          let rule = appliedRule.rule;
                                          if (!rule && (item.product as any)?.rules) {
                                            rule = (item.product as any).rules.find((r: any) => r.id === appliedRule.rule_id);
                                          }
                                          if (!rule) rule = appliedRule;
                                          if (!rule || !rule.rule_name) return null;

                                          const modifier = parseFloat(rule.price_modifier);
                                          const isPositive = modifier > 0;
                                          const isNegative = modifier < 0;

                                          return (
                                            <div key={idx} className="inline-flex items-center px-1.5 py-0.5 rounded bg-purple-50 text-[9px] text-purple-700 border border-purple-100">
                                              <span className="font-bold mr-1">{rule.rule_name}:</span>
                                              <span className={isPositive ? "text-emerald-600" : isNegative ? "text-amber-600" : ""}>
                                                {rule.price_type === "percentage" 
                                                  ? `${modifier > 0 ? "+" : ""}${modifier}%` 
                                                  : `${modifier > 0 ? "+" : "-"}${opp.currency?.symbol}${formatCurrency(Math.abs(modifier))}`}
                                              </span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ) : hasExtras && (
                                      <div className="text-[9px] text-purple-600 font-bold mt-1 flex items-center gap-1">
                                        <Plus className="h-2.5 w-2.5" />
                                        {opp.currency?.symbol}{formatCurrency(lineTotal - (requestedPrice * qty))} en extras aplicados
                                      </div>
                                    )}
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        </TableCell>
                        <TableCell className="text-right text-slate-500 text-sm">
                          {opp.currency?.symbol}{formatCurrency(baseOriginal)}
                        </TableCell>
                        <TableCell className="text-right font-medium text-emerald-600">
                          {totalIncrease > 0 ? `+ ${opp.currency?.symbol}${formatCurrency(totalIncrease)}` : '-'}
                        </TableCell>
                        <TableCell className="text-right font-medium text-amber-600">
                          {totalDiscount > 0 ? `- ${opp.currency?.symbol}${formatCurrency(totalDiscount)}` : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex flex-col items-end">
                            <span className="font-black text-slate-900">
                              {opp.currency?.symbol}{formatCurrency(requested)}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="hover:bg-blue-50 hover:text-blue-600"
                            onClick={() => {
                              setSelectedOppId(opp.id);
                              setIsDetailOpen(true);
                            }}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            Revisar
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>

      {selectedOppId && (
        <OpportunityDetail
          isOpen={isDetailOpen}
          onClose={() => {
            setIsDetailOpen(false);
            fetchPendingApprovals(); // Refrescar lista al cerrar
          }}
          opportunityId={selectedOppId}
          slug={slug || ""}
          onOpportunityUpdated={fetchPendingApprovals}
        />
      )}
    </div>
  );
}

